import dayjs, { ConfigType, Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import isoWeek from 'dayjs/plugin/isoWeek'

dayjs.extend(utc)
dayjs.extend(isoWeek)

/**
 * Anchors a date to the implementation's timezone (company.gmtZone, expressed in hours),
 * so trip dates are read and rendered in the country's local time (Argentina, Colombia, ...)
 * regardless of the timezone of the user's device.
 *
 * withdrawalDate and friends are persisted as UTC instants; this only affects how they are
 * displayed/bucketed, never how they are stored.
 *
 * Always go through a native Date first: calling utcOffset() again on a Dayjs that already
 * has a fixed offset corrupts the wall clock (dayjs quirk) and breaks week navigation.
 */
const inImplementationZone = (date: ConfigType, gmtZone = 0): Dayjs =>
  dayjs(dayjs.isDayjs(date) ? date.toDate() : date).utcOffset(gmtZone)

/**
 * Calendar day (YYYY-MM-DD) used to build company-local day bounds.
 * - Dayjs: trust the wall clock already on the instance (picker selection or company-anchored).
 * - Date/string: read the day in company.gmtZone so re-normalizing filters is idempotent
 *   even when the browser OS timezone differs from the company.
 */
const toCompanyCalendarDate = (date: ConfigType, gmtZone = 0): string => {
  if (dayjs.isDayjs(date)) {
    return date.format('YYYY-MM-DD')
  }
  return inImplementationZone(date, gmtZone).format('YYYY-MM-DD')
}

/**
 * Interprets the calendar day of `date` as start of day in the company timezone.
 * Picker selections keep their displayed YYYY-MM-DD; UTC instants use company local day.
 */
const companyStartOfDay = (date: ConfigType, gmtZone = 0): Dayjs => {
  const calendarDate = toCompanyCalendarDate(date, gmtZone)
  return dayjs(calendarDate).utcOffset(gmtZone, true).startOf('day')
}

/**
 * Interprets the calendar day of `date` as end of day in the company timezone.
 */
const companyEndOfDay = (date: ConfigType, gmtZone = 0): Dayjs => {
  const calendarDate = toCompanyCalendarDate(date, gmtZone)
  return dayjs(calendarDate).utcOffset(gmtZone, true).endOf('day')
}

const companyStartOfMonth = (date: ConfigType, gmtZone = 0): Dayjs => {
  const calendarMonth = toCompanyCalendarDate(date, gmtZone).slice(0, 7)
  return dayjs(`${calendarMonth}-01`).utcOffset(gmtZone, true).startOf('month')
}

/** Monday 00:00 of the ISO week that contains `date`, in the company timezone. */
const companyStartOfIsoWeek = (date: ConfigType, gmtZone = 0): Dayjs =>
  companyStartOfDay(inImplementationZone(date, gmtZone).isoWeekday(1), gmtZone)

/** Start of "today" in the company timezone (ignores OS timezone). */
const companyStartOfToday = (gmtZone = 0): Dayjs => {
  const calendarDate = inImplementationZone(dayjs(), gmtZone).format('YYYY-MM-DD')
  return dayjs(calendarDate).utcOffset(gmtZone, true).startOf('day')
}

const formatInCompanyZone = (date: ConfigType, gmtZone = 0, format = 'H:mm'): string =>
  inImplementationZone(date, gmtZone).format(format)

export const ImplementationTimeUtils = {
  inImplementationZone,
  companyStartOfDay,
  companyEndOfDay,
  companyStartOfMonth,
  companyStartOfIsoWeek,
  companyStartOfToday,
  formatInCompanyZone,
}
